import { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Upload, Search, CheckCircle2, XCircle, AlertTriangle, Link2, IndianRupee,
  Calendar, Filter, Download, RefreshCw, Smartphone, ArrowUpRight, ArrowDownRight,
  FileText, BarChart3, Clock, Eye
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAccounting } from "@/contexts/AccountingContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend
} from "recharts";
import * as XLSX from "xlsx";

// ── Types ───────────────────────────────────────────────────────────────

type UPIProvider = "Google Pay" | "PhonePe" | "Paytm" | "BHIM" | "Other";
type MatchStatus = "Matched" | "Unmatched" | "Manually Matched" | "Ignored";

interface UPITransaction {
  id: string;
  date: string;
  upiId: string;
  payerName: string;
  amount: number;
  type: "Credit" | "Debit";
  provider: UPIProvider;
  referenceNumber: string;
  status: MatchStatus;
  matchedInvoiceId?: string;
  matchedInvoiceNumber?: string;
  description: string;
}

// ── UPI Detection ───────────────────────────────────────────────────────

const UPI_PATTERNS: { pattern: RegExp; provider: UPIProvider }[] = [
  { pattern: /google\s*pay|gpay|goog/i, provider: "Google Pay" },
  { pattern: /phonepe|phpe/i, provider: "PhonePe" },
  { pattern: /paytm/i, provider: "Paytm" },
  { pattern: /bhim|upi/i, provider: "BHIM" },
];

function detectUPIProvider(description: string): UPIProvider | null {
  for (const { pattern, provider } of UPI_PATTERNS) {
    if (pattern.test(description)) return provider;
  }
  if (/@/.test(description) && /upi|ybl|okhdfcbank|okicici|oksbi|apl|paytm/i.test(description)) return "Other";
  return null;
}

function parseUPITransactions(rows: string[][]): UPITransaction[] {
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.toLowerCase().trim());

  const dateIdx = header.findIndex((h) => /date/i.test(h));
  const amtIdx = header.findIndex((h) => /amount|value|debit|credit/i.test(h));
  const descIdx = header.findIndex((h) => /desc|narr|particular|remark|detail/i.test(h));
  const refIdx = header.findIndex((h) => /ref|txn|transaction|utr/i.test(h));
  const typeIdx = header.findIndex((h) => /type|cr\/dr|dr\/cr/i.test(h));
  const creditIdx = header.findIndex((h) => /^credit$/i.test(h));
  const debitIdx = header.findIndex((h) => /^debit$/i.test(h));

  if (dateIdx === -1 || (amtIdx === -1 && creditIdx === -1)) return [];

  const txns: UPITransaction[] = [];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row[dateIdx]) continue;

    const desc = row[descIdx] || "";
    const provider = detectUPIProvider(desc);
    if (!provider) continue; // Skip non-UPI transactions

    let amount = 0;
    let type: "Credit" | "Debit" = "Credit";

    if (creditIdx !== -1 && debitIdx !== -1) {
      const cr = parseFloat(row[creditIdx]?.replace(/[^0-9.-]/g, "") || "0");
      const dr = parseFloat(row[debitIdx]?.replace(/[^0-9.-]/g, "") || "0");
      if (cr > 0) { amount = cr; type = "Credit"; }
      else { amount = dr; type = "Debit"; }
    } else {
      amount = Math.abs(parseFloat(row[amtIdx]?.replace(/[^0-9.-]/g, "") || "0"));
      if (typeIdx !== -1 && /dr|debit/i.test(row[typeIdx] || "")) type = "Debit";
      else if (amount < 0) { amount = Math.abs(amount); type = "Debit"; }
    }

    if (amount === 0) continue;

    // Extract UPI ID from description
    const upiMatch = desc.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9]+)/);

    txns.push({
      id: `upi-${Date.now()}-${i}`,
      date: row[dateIdx],
      upiId: upiMatch?.[1] || "",
      payerName: desc.replace(/upi.*$/i, "").replace(/[/-]/g, " ").trim().slice(0, 40) || "Unknown",
      amount,
      type,
      provider,
      referenceNumber: row[refIdx] || `UTR${Date.now()}${i}`,
      status: "Unmatched",
      description: desc,
    });
  }
  return txns;
}

// ── Card Component ──────────────────────────────────────────────────────

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-4 sm:p-5 rounded-lg bg-card border border-border shadow-card ${className}`}>{children}</div>
);

const PROVIDER_COLORS: Record<UPIProvider, string> = {
  "Google Pay": "hsl(var(--accent))",
  "PhonePe": "hsl(270 60% 55%)",
  "Paytm": "hsl(200 80% 50%)",
  "BHIM": "hsl(var(--primary))",
  "Other": "hsl(var(--muted-foreground))",
};

// ── Main Component ──────────────────────────────────────────────────────

const UPIReconciliationPage = () => {
  const { vouchers } = useAccounting();
  const { toast } = useToast();

  const [transactions, setTransactions] = useState<UPITransaction[]>([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterProvider, setFilterProvider] = useState<string>("all");
  const [manualMatchDialog, setManualMatchDialog] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState("");

  // Get invoices (sales vouchers)
  const invoices = useMemo(() =>
    vouchers.filter((v) => v.type === "Sales" && v.status === "Approved"),
    [vouchers]
  );

  // ── Import Handlers ─────────────────────────────────────────────────

  const processCSVText = useCallback((text: string) => {
    const rows = text.split("\n").map((line) => line.split(",").map((c) => c.trim().replace(/^"|"$/g, "")));
    const txns = parseUPITransactions(rows);
    if (txns.length === 0) {
      toast({ title: "No UPI transactions found", description: "Ensure the file contains UPI payment data with recognizable descriptions.", variant: "destructive" });
      return;
    }
    setTransactions(txns);
    toast({ title: `${txns.length} UPI transactions imported`, description: `Detected from ${new Set(txns.map((t) => t.provider)).size} providers` });
  }, [toast]);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.name.endsWith(".csv") || file.name.endsWith(".txt")) {
      const text = await file.text();
      processCSVText(text);
    } else if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      const txns = parseUPITransactions(rows);
      if (txns.length === 0) {
        toast({ title: "No UPI transactions found", description: "Could not detect UPI payments in the Excel file.", variant: "destructive" });
        return;
      }
      setTransactions(txns);
      toast({ title: `${txns.length} UPI transactions imported` });
    } else {
      toast({ title: "Unsupported format", description: "Please upload a CSV or Excel file.", variant: "destructive" });
    }
    e.target.value = "";
  }, [processCSVText, toast]);

  // ── Auto-Match ─────────────────────────────────────────────────────

  const runAutoMatch = useCallback(() => {
    let matchCount = 0;
    const updated = transactions.map((txn) => {
      if (txn.status !== "Unmatched" || txn.type !== "Credit") return txn;

      // Find invoice with matching amount
      const match = invoices.find((inv) => {
        const invAmount = inv.totalDebit;
        const diff = Math.abs(invAmount - txn.amount);
        // Match within ₹1 tolerance
        return diff <= 1 && !transactions.some(
          (t) => t.id !== txn.id && (t.status === "Matched" || t.status === "Manually Matched") && t.matchedInvoiceId === inv.id
        );
      });

      if (match) {
        matchCount++;
        return {
          ...txn,
          status: "Matched" as MatchStatus,
          matchedInvoiceId: match.id,
          matchedInvoiceNumber: match.invoiceNumber || match.voucherNumber,
        };
      }
      return txn;
    });

    setTransactions(updated);
    toast({
      title: "Auto-match complete",
      description: `${matchCount} payment${matchCount !== 1 ? "s" : ""} matched with invoices. ${updated.filter((t) => t.status === "Unmatched").length} still unmatched.`,
    });
  }, [transactions, invoices, toast]);

  // ── Manual Match ──────────────────────────────────────────────────

  const confirmManualMatch = useCallback(() => {
    if (!manualMatchDialog || !selectedInvoice) return;
    const invoice = invoices.find((inv) => inv.id === selectedInvoice);
    setTransactions((prev) => prev.map((t) =>
      t.id === manualMatchDialog ? {
        ...t,
        status: "Manually Matched" as MatchStatus,
        matchedInvoiceId: selectedInvoice,
        matchedInvoiceNumber: invoice?.invoiceNumber || invoice?.voucherNumber,
      } : t
    ));
    setManualMatchDialog(null);
    setSelectedInvoice("");
    toast({ title: "Payment matched", description: `Linked to invoice ${invoice?.invoiceNumber || invoice?.voucherNumber}` });
  }, [manualMatchDialog, selectedInvoice, invoices, toast]);

  const ignoreTransaction = useCallback((id: string) => {
    setTransactions((prev) => prev.map((t) => t.id === id ? { ...t, status: "Ignored" as MatchStatus } : t));
  }, []);

  // ── Filtered Transactions ─────────────────────────────────────────

  const filtered = useMemo(() => {
    return transactions.filter((t) => {
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      if (filterProvider !== "all" && t.provider !== filterProvider) return false;
      if (search && !t.payerName.toLowerCase().includes(search.toLowerCase()) &&
          !t.referenceNumber.toLowerCase().includes(search.toLowerCase()) &&
          !t.upiId.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [transactions, filterStatus, filterProvider, search]);

  // ── Stats ──────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const credits = transactions.filter((t) => t.type === "Credit");
    const matched = transactions.filter((t) => t.status === "Matched" || t.status === "Manually Matched");
    const unmatched = transactions.filter((t) => t.status === "Unmatched");

    const totalReceived = credits.reduce((s, t) => s + t.amount, 0);
    const matchedAmount = matched.reduce((s, t) => s + t.amount, 0);
    const unmatchedAmount = unmatched.filter((t) => t.type === "Credit").reduce((s, t) => s + t.amount, 0);

    const providerBreakdown = Object.entries(
      credits.reduce<Record<string, number>>((acc, t) => {
        acc[t.provider] = (acc[t.provider] || 0) + t.amount;
        return acc;
      }, {})
    ).map(([name, value]) => ({ name, value, color: PROVIDER_COLORS[name as UPIProvider] || "hsl(var(--muted-foreground))" }));

    // Daily collections
    const dailyMap = credits.reduce<Record<string, number>>((acc, t) => {
      const day = t.date.slice(0, 10);
      acc[day] = (acc[day] || 0) + t.amount;
      return acc;
    }, {});
    const dailyCollections = Object.entries(dailyMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-14)
      .map(([day, amount]) => ({ day: day.slice(5), amount }));

    return {
      total: transactions.length,
      totalReceived,
      matched: matched.length,
      matchedAmount,
      unmatched: unmatched.length,
      unmatchedAmount,
      providerBreakdown,
      dailyCollections,
      matchRate: transactions.length > 0 ? Math.round((matched.length / Math.max(credits.length, 1)) * 100) : 0,
    };
  }, [transactions]);

  // ── Export ─────────────────────────────────────────────────────────

  const exportReport = useCallback(() => {
    const wsData = [
      ["Date", "Payer", "UPI ID", "Amount", "Type", "Provider", "Reference", "Status", "Matched Invoice"],
      ...transactions.map((t) => [
        t.date, t.payerName, t.upiId, t.amount, t.type, t.provider, t.referenceNumber, t.status, t.matchedInvoiceNumber || ""
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "UPI Reconciliation");
    XLSX.writeFile(wb, `UPI_Reconciliation_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast({ title: "Report exported" });
  }, [transactions, toast]);

  // ── Load Demo Data ────────────────────────────────────────────────

  const loadDemoData = () => {
    const demoTxns: UPITransaction[] = [
      { id: "d1", date: "2026-03-01", upiId: "merchant@okicici", payerName: "Mehta Traders", amount: 10000, type: "Credit", provider: "Google Pay", referenceNumber: "UTR2026030101", status: "Unmatched", description: "UPI/Google Pay/Mehta Traders" },
      { id: "d2", date: "2026-03-01", upiId: "shop@ybl", payerName: "Sunrise Textiles", amount: 25000, type: "Credit", provider: "PhonePe", referenceNumber: "UTR2026030102", status: "Unmatched", description: "UPI/PhonePe/Sunrise Textiles" },
      { id: "d3", date: "2026-03-02", upiId: "sales@paytm", payerName: "Delhi Exports", amount: 15500, type: "Credit", provider: "Paytm", referenceNumber: "UTR2026030201", status: "Unmatched", description: "UPI/Paytm/Delhi Exports" },
      { id: "d4", date: "2026-03-02", upiId: "firm@okicici", payerName: "Bharat Motors", amount: 42000, type: "Credit", provider: "BHIM", referenceNumber: "UTR2026030202", status: "Unmatched", description: "UPI/BHIM/Bharat Motors" },
      { id: "d5", date: "2026-03-03", upiId: "pay@ybl", payerName: "Hyderabad Pharma", amount: 8750, type: "Credit", provider: "Google Pay", referenceNumber: "UTR2026030301", status: "Unmatched", description: "UPI/GPay/Hyderabad Pharma" },
      { id: "d6", date: "2026-03-03", upiId: "vendor@oksbi", payerName: "Patel & Co.", amount: 5200, type: "Credit", provider: "PhonePe", referenceNumber: "UTR2026030302", status: "Unmatched", description: "UPI/PhonePe/Patel & Co." },
      { id: "d7", date: "2026-03-04", upiId: "buy@apl", payerName: "Raj Industries", amount: 18000, type: "Credit", provider: "Paytm", referenceNumber: "UTR2026030401", status: "Unmatched", description: "UPI/Paytm/Raj Industries" },
      { id: "d8", date: "2026-03-04", upiId: "office@okicici", payerName: "Unknown Sender", amount: 3200, type: "Credit", provider: "BHIM", referenceNumber: "UTR2026030402", status: "Unmatched", description: "UPI/BHIM UPI payment" },
      { id: "d9", date: "2026-03-05", upiId: "shop@ybl", payerName: "Sunrise Textiles", amount: 12000, type: "Credit", provider: "Google Pay", referenceNumber: "UTR2026030501", status: "Unmatched", description: "UPI/Google Pay/Sunrise Textiles" },
      { id: "d10", date: "2026-03-05", upiId: "bill@paytm", payerName: "Electricity Board", amount: 4500, type: "Debit", provider: "Paytm", referenceNumber: "UTR2026030502", status: "Unmatched", description: "UPI/Paytm/EB Bill Payment" },
    ];
    setTransactions(demoTxns);
    toast({ title: "Demo data loaded", description: "10 sample UPI transactions added" });
  };

  const statusColors: Record<MatchStatus, string> = {
    Matched: "text-success bg-success/10 border-success/30",
    "Manually Matched": "text-accent bg-accent/10 border-accent/30",
    Unmatched: "text-warning bg-warning/10 border-warning/30",
    Ignored: "text-muted-foreground bg-muted/50 border-border",
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Smartphone className="h-6 w-6 text-accent" />
            UPI Payment Reconciliation
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Import UPI statements, auto-match with invoices, reconcile payments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadDemoData}>
            <Eye className="h-4 w-4 mr-1" /> Demo Data
          </Button>
          <label>
            <Button variant="outline" size="sm" asChild>
              <span><Upload className="h-4 w-4 mr-1" /> Import Statement</span>
            </Button>
            <input type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden" onChange={handleFileUpload} />
          </label>
          {transactions.length > 0 && (
            <>
              <Button size="sm" onClick={runAutoMatch}>
                <RefreshCw className="h-4 w-4 mr-1" /> Auto-Match
              </Button>
              <Button variant="outline" size="sm" onClick={exportReport}>
                <Download className="h-4 w-4 mr-1" /> Export
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {transactions.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: "Total UPI Payments", value: stats.total, sub: `₹${stats.totalReceived.toLocaleString("en-IN")}`, icon: IndianRupee, color: "text-accent" },
            { label: "Matched", value: stats.matched, sub: `₹${stats.matchedAmount.toLocaleString("en-IN")}`, icon: CheckCircle2, color: "text-success" },
            { label: "Unmatched", value: stats.unmatched, sub: `₹${stats.unmatchedAmount.toLocaleString("en-IN")}`, icon: AlertTriangle, color: "text-warning" },
            { label: "Match Rate", value: `${stats.matchRate}%`, sub: "Auto + Manual", icon: Link2, color: "text-accent" },
            { label: "Providers", value: stats.providerBreakdown.length, sub: "Active UPI apps", icon: Smartphone, color: "text-primary" },
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">{stat.label}</span>
                  <stat.icon className={`h-4 w-4 ${stat.color}`} />
                </div>
                <div className="text-xl font-bold font-display text-foreground">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-0.5">{stat.sub}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Main Content */}
      <Tabs defaultValue="transactions" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="reports" disabled={transactions.length === 0}>Reports</TabsTrigger>
        </TabsList>

        {/* ── Transactions Tab ── */}
        <TabsContent value="transactions" className="space-y-4">
          {transactions.length === 0 ? (
            <Card className="text-center py-16">
              <Smartphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Import UPI Payment Statement</h3>
              <p className="text-sm text-muted-foreground mb-1">Upload a CSV or Excel file from your bank containing UPI transactions.</p>
              <p className="text-xs text-muted-foreground mb-6">The system auto-detects Google Pay, PhonePe, Paytm, and BHIM payments.</p>
              <div className="flex items-center justify-center gap-3">
                <label>
                  <Button asChild><span><Upload className="h-4 w-4 mr-2" /> Upload Statement</span></Button>
                  <input type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden" onChange={handleFileUpload} />
                </label>
                <Button variant="outline" onClick={loadDemoData}><Eye className="h-4 w-4 mr-2" /> Load Demo Data</Button>
              </div>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-lg mx-auto">
                {(["Google Pay", "PhonePe", "Paytm", "BHIM"] as UPIProvider[]).map((p) => (
                  <div key={p} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 border border-border/50">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PROVIDER_COLORS[p] }} />
                    <span className="text-xs text-muted-foreground">{p}</span>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <>
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px] max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search payer, UPI ID, reference..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[150px]"><Filter className="h-3.5 w-3.5 mr-1" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Matched">Matched</SelectItem>
                    <SelectItem value="Unmatched">Unmatched</SelectItem>
                    <SelectItem value="Manually Matched">Manual</SelectItem>
                    <SelectItem value="Ignored">Ignored</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterProvider} onValueChange={setFilterProvider}>
                  <SelectTrigger className="w-[150px]"><Smartphone className="h-3.5 w-3.5 mr-1" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Providers</SelectItem>
                    <SelectItem value="Google Pay">Google Pay</SelectItem>
                    <SelectItem value="PhonePe">PhonePe</SelectItem>
                    <SelectItem value="Paytm">Paytm</SelectItem>
                    <SelectItem value="BHIM">BHIM</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">{filtered.length} of {transactions.length}</span>
              </div>

              {/* Transaction Table */}
              <Card className="p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        {["Date", "Payer / UPI ID", "Amount", "Provider", "Reference", "Status", "Invoice", "Actions"].map((h) => (
                          <th key={h} className="text-left py-3 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((txn) => (
                        <tr key={txn.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                          <td className="py-3 px-3 text-muted-foreground whitespace-nowrap">{txn.date}</td>
                          <td className="py-3 px-3">
                            <div className="font-medium text-foreground">{txn.payerName}</div>
                            {txn.upiId && <div className="text-xs text-muted-foreground">{txn.upiId}</div>}
                          </td>
                          <td className="py-3 px-3 font-semibold whitespace-nowrap">
                            <span className={txn.type === "Credit" ? "text-success" : "text-destructive"}>
                              {txn.type === "Credit" ? "+" : "-"}₹{txn.amount.toLocaleString("en-IN")}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PROVIDER_COLORS[txn.provider] }} />
                              <span className="text-xs text-foreground">{txn.provider}</span>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-xs text-muted-foreground font-mono">{txn.referenceNumber}</td>
                          <td className="py-3 px-3">
                            <Badge variant="outline" className={`text-[10px] ${statusColors[txn.status]}`}>
                              {txn.status === "Matched" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                              {txn.status === "Unmatched" && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {txn.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-xs">
                            {txn.matchedInvoiceNumber ? (
                              <span className="text-accent font-medium">{txn.matchedInvoiceNumber}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            {txn.status === "Unmatched" && txn.type === "Credit" && (
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setManualMatchDialog(txn.id); setSelectedInvoice(""); }}>
                                  <Link2 className="h-3 w-3 mr-1" /> Match
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => ignoreTransaction(txn.id)}>
                                  <XCircle className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">No transactions match your filters.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ── Reports Tab ── */}
        <TabsContent value="reports" className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Daily Collections */}
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-accent" /> Daily UPI Collections
              </h3>
              {stats.dailyCollections.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={stats.dailyCollections}>
                    <defs>
                      <linearGradient id="upiGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v: number) => [`₹${v.toLocaleString("en-IN")}`, "Collection"]} />
                    <Area type="monotone" dataKey="amount" stroke="hsl(var(--accent))" fill="url(#upiGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No collection data yet</p>
              )}
            </Card>

            {/* Provider Breakdown */}
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-accent" /> UPI Provider Breakdown
              </h3>
              {stats.providerBreakdown.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={stats.providerBreakdown} cx="50%" cy="50%" innerRadius={42} outerRadius={68} paddingAngle={3} dataKey="value">
                        {stats.providerBreakdown.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip formatter={(v: number) => [`₹${v.toLocaleString("en-IN")}`, ""]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 mt-2">
                    {stats.providerBreakdown.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                          <span className="text-muted-foreground">{p.name}</span>
                        </div>
                        <span className="font-medium text-foreground">₹{p.value.toLocaleString("en-IN")}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No provider data yet</p>
              )}
            </Card>
          </div>

          {/* Summary Table */}
          <Card>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-accent" /> UPI Payments Summary
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-success/5 border border-success/20">
                <ArrowUpRight className="h-4 w-4 text-success mb-2" />
                <div className="text-lg font-bold text-foreground">₹{stats.totalReceived.toLocaleString("en-IN")}</div>
                <div className="text-xs text-muted-foreground">Total Received (UPI)</div>
              </div>
              <div className="p-4 rounded-lg bg-accent/5 border border-accent/20">
                <CheckCircle2 className="h-4 w-4 text-accent mb-2" />
                <div className="text-lg font-bold text-foreground">₹{stats.matchedAmount.toLocaleString("en-IN")}</div>
                <div className="text-xs text-muted-foreground">Matched with Invoices</div>
              </div>
              <div className="p-4 rounded-lg bg-warning/5 border border-warning/20">
                <AlertTriangle className="h-4 w-4 text-warning mb-2" />
                <div className="text-lg font-bold text-foreground">₹{stats.unmatchedAmount.toLocaleString("en-IN")}</div>
                <div className="text-xs text-muted-foreground">Unmatched Payments</div>
              </div>
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                <Link2 className="h-4 w-4 text-primary mb-2" />
                <div className="text-lg font-bold text-foreground">{stats.matchRate}%</div>
                <div className="text-xs text-muted-foreground">Overall Match Rate</div>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Manual Match Dialog */}
      <Dialog open={!!manualMatchDialog} onOpenChange={() => setManualMatchDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Match UPI Payment with Invoice</DialogTitle>
          </DialogHeader>
          {manualMatchDialog && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <p className="text-sm font-medium text-foreground">
                  {transactions.find((t) => t.id === manualMatchDialog)?.payerName}
                </p>
                <p className="text-lg font-bold text-success">
                  +₹{transactions.find((t) => t.id === manualMatchDialog)?.amount.toLocaleString("en-IN")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {transactions.find((t) => t.id === manualMatchDialog)?.provider} • {transactions.find((t) => t.id === manualMatchDialog)?.date}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Select Invoice</label>
                <Select value={selectedInvoice} onValueChange={setSelectedInvoice}>
                  <SelectTrigger><SelectValue placeholder="Choose an invoice..." /></SelectTrigger>
                  <SelectContent>
                    {invoices.map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.invoiceNumber || inv.voucherNumber} — ₹{inv.totalDebit.toLocaleString("en-IN")} ({inv.partyName || "Unknown"})
                      </SelectItem>
                    ))}
                    {invoices.length === 0 && (
                      <div className="text-sm text-muted-foreground p-3 text-center">No approved invoices available</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualMatchDialog(null)}>Cancel</Button>
            <Button onClick={confirmManualMatch} disabled={!selectedInvoice}>Confirm Match</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UPIReconciliationPage;
